import React, {
	forwardRef,
	useState,
	useImperativeHandle,
	useEffect,
	useCallback,
} from "react";

import { Query } from "react-apollo";
import gql from "graphql-tag";

import Context from "shared/util/Context";
import Str from "shared/components/Str";
import ClickWatcher from "shared/components/ClickWatcher";
import noop from "shared/util/noop";

import {
	OKEVENT,
	subscribeToOkEvent,
	unsubscribeFromOkEvent,
} from "shared/util/OkEventHelpers";

import { MESSAGE_FOLDERS } from "apps/messages/util/constants";
import Messages from "apps/messages/components/Messages";
import ErrorMessage from "apps/messages/components/ErrorMessage";

import "./Dropdown.scss";

const SCROLL_ID = "messagesdropdown-scroll-container";
const QUERY = gql`
	${Messages.fragments.conversations}

	query getIncomingMessages (
		$userid: String!,
		$conversationsFilter: ConversationFilter!
		$conversationsAfter: String
	) {
		user(id: $userid) {
			...Conversations
		}
	}
`;

const Dropdown = forwardRef((props, ref) => {
	const [isOpen, setIsOpen] = useState(true);
	const [hiddenUsers, setHiddenUsers] = useState({});

	const close = useCallback(() => setIsOpen(false), [setIsOpen]);

	useEffect(() => {
		const hideUser = (params) => {
			setHiddenUsers({
				...hiddenUsers,
				[params.tuid]: true,
			});
		};

		if (isOpen) {
			window.addEventListener("headerbar.usermenu.open", close);
			subscribeToOkEvent(OKEVENT.ProfileActionBar, close);
		} else {
			window.removeEventListener("headerbar.usermenu.open", close);
			unsubscribeFromOkEvent(OKEVENT.ProfileActionBar, close);
		}

		subscribeToOkEvent(OKEVENT.Unmatch, hideUser);
		subscribeToOkEvent(OKEVENT.Block, hideUser);

		return () => {
			if (isOpen) {
				window.removeEventListener("headerbar.usermenu.open", close);
				unsubscribeFromOkEvent(OKEVENT.ProfileActionBar, close);
				unsubscribeFromOkEvent(OKEVENT.Unmatch, hideUser);
				unsubscribeFromOkEvent(OKEVENT.Block, hideUser);
			}
		};
	}, [isOpen, close, hiddenUsers, setHiddenUsers]);

	useEffect(() => {
		window.ScrollHelpers.preventOverscroll(SCROLL_ID);
	}, []);

	useImperativeHandle(ref, () => ({ setIsOpen }));

	if (!isOpen) {
		return null;
	}

	return (
		<ClickWatcher
			className="messagedropdown"
			onOutsideClick={close}
			active={isOpen}
		>
			<a href="/messages" className="messagedropdown-header">
				<Str>Conversations</Str>
			</a>

			<div className="messagedropdown-body" id={SCROLL_ID}>
				<Query
					query={QUERY}
					variables={{
						userid: Context.get("userid"),
						conversationsFilter: MESSAGE_FOLDERS.INCOMING,
					}}
				>
					{({ data, loading, error }) => {
						if (error) {
							return (
								<ErrorMessage>
									<Str>There was a problem loading your messages!</Str>
								</ErrorMessage>
							);
						}

						const { user = {} } = data;

						return (
							<Messages
								filter={MESSAGE_FOLDERS.INCOMING}
								fetchMore={noop}
								conversations={user.conversations}
								hiddenUsers={hiddenUsers}
								loading={loading}
								onMessageOpen={close}
								picBorderWidth={2}
								hasInfiniteScroll={false}
							/>
						);
					}}
				</Query>
			</div>

			<a href="/messages" className="messagedropdown-see-all">
				<Str>See all</Str>
			</a>
		</ClickWatcher>
	);
});

export default Dropdown;



// WEBPACK FOOTER //
// ./src/apps/messages/components/Dropdown.jsx